import { motion } from "framer-motion";
import { useConfig } from "../context/useConfig";
import { SafeImage } from "./SafeImage";



// Central animation variants
const fadeIn = (delay = 0, y = 24) => ({
  hidden: { opacity: 0, y },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 0.66, 0.45, 0.99], delay } }
});

const scaleIn = (delay = 0) => ({
  hidden: { opacity: 0, scale: 0.85 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: [0.25, 0.8, 0.25, 1], delay } }
});

// Decorative gradient blob (single element reused)
const GradientBlob = ({ className }) => (
  <div aria-hidden className={`pointer-events-none absolute mix-blend-multiply blur-3xl opacity-25 ${className}`} />
);

const ActionButton = ({ variant = "primary", label, onClick, href, download }) => {
  const base = "relative group inline-flex items-center justify-center px-5 py-3 rounded-xl font-semibold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 transition overflow-hidden";
  const variants = {
  primary: "bg-accent-gradient text-white shadow hover:shadow-lg",
  secondary: "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white shadow hover:shadow-lg",
  neutral: "bg-gradient-to-r from-gray-700 to-gray-900 text-white shadow hover:shadow-lg",
  accent: "bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow hover:shadow-lg"
  };
  const cls = `${base} ${variants[variant] || variants.primary}`;
  const content = <span className="relative z-10 tracking-wide">{label}</span>;
  if (href) {
    return (
      <motion.a variants={scaleIn()} href={href} download={download} className={cls} aria-label={label}>
        <span className="absolute inset-0 opacity-0 group-hover:opacity-25 bg-white/20 transition-opacity" />
        {content}
      </motion.a>
    );
  }
  return (
    <motion.button variants={scaleIn()} onClick={onClick} className={cls} aria-label={label}>
      <span className="absolute inset-0 opacity-0 group-hover:opacity-25 bg-white/20 transition-opacity" />
      {content}
    </motion.button>
  );
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 72; // offset for navbar
  window.scrollTo({ top: y, behavior: "smooth" });
};

function About() {
  const { config, isLoading, error } = useConfig();
  
  // Call all hooks BEFORE any conditional returns (Rules of Hooks)
  const buttons = config.aboutSection?.buttons || {};
  const heading = config.aboutSection?.heading || "ABOUT";
  const actionsHeading = config.aboutSection?.actionsHeading || "Explore More";
  const highlightsLabel = config.ui?.highlightsLabel || "Highlights";
  
  // Show loading state while config is being fetched
  if (isLoading || !config) {
    return (
      <section id="about" aria-labelledby="about-heading" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-16 items-start">
            <div className="lg:col-span-5 flex flex-col gap-10">
              <div className="aspect-square w-full max-w-sm mx-auto rounded-3xl overflow-hidden shadow-xl bg-gray-100 animate-pulse" />
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
            <div className="lg:col-span-7 space-y-10">
              <div className="space-y-4">
                <div className="h-12 bg-gray-200 rounded animate-pulse w-48" />
                <div className="h-6 bg-gray-100 rounded animate-pulse w-96" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section id="about" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-600">Error loading content. Please refresh the page.</p>
        </div>
      </section>
    );
  }

  return (
  <section id="about" aria-labelledby="about-heading" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden">
      {/* Single set of ambient layers instead of many scattered elements */}
      <GradientBlob className="w-96 h-96 top-[-10%] left-[-5%] bg-indigo-300" />
      <GradientBlob className="w-96 h-96 bottom-[-15%] right-[-5%] bg-purple-300" />
  <div className="absolute inset-0 pattern-dots" aria-hidden />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} className="space-y-20">
          {/* Header */}
          <motion.header variants={fadeIn(0)} className="text-center max-w-3xl mx-auto">
            <h2 id="about-heading" className="text-4xl sm:text-5xl font-extrabold tracking-tight gradient-text">
              {heading}
            </h2>
            {config.site?.tagline && (
              <p className="mt-6 text-lg text-gray-600 font-medium leading-relaxed">
                {config.site.tagline}
              </p>
            )}
          </motion.header>

          {/* Body Layout */}
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-16 items-start">
            {/* Profile Column */}
            <motion.div variants={fadeIn(0.05)} className="lg:col-span-5 flex flex-col gap-10">
              <figure className="relative group">
                <div className="aspect-square w-full max-w-sm mx-auto rounded-3xl overflow-hidden shadow-xl ring-1 ring-black/5 bg-gradient-to-br from-slate-100 to-white">
                  <SafeImage
                    src={config.about?.avatarUrl || config.profileImage}
                    alt={config.site?.name || "Profile avatar"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <figcaption className="sr-only">{config.site?.name}</figcaption>
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.35),transparent_60%)]" />
              </figure>

              {/* Actions — under the picture */}
              <motion.div variants={fadeIn(0.1)} className="space-y-4">
                <h4 className="text-sm font-semibold tracking-wider text-gray-500 uppercase">{actionsHeading}</h4>
                <motion.div variants={fadeIn(0.15)} className="grid gap-3 grid-cols-2">
                  <ActionButton
                    variant="primary"
                    label={buttons.skills || "Skills"}
                    onClick={() => scrollTo("skills")}
                  />
                  <ActionButton
                    variant="accent"
                    label={buttons.projects || "Projects"}
                    onClick={() => scrollTo("projects")}
                  />
                  <ActionButton
                    variant="secondary"
                    label={buttons.experience || "Experience"}
                    onClick={() => scrollTo("experience")}
                  />
                  {config.resume?.url && (
                    <ActionButton
                      variant="neutral"
                      label={buttons.resume || "Resume"}
                      href={config.resume.url}
                      download={config.resume.filename}
                    />
                  )}
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Content Column */}
            <div className="lg:col-span-7 space-y-12">
              {/* Bio & Highlights Card */}
              <motion.div
                variants={fadeIn(0.15)}
                className="relative glass-panel glass-panel-hover p-8 sm:p-10"
              >
                <div className="flex flex-col gap-6">
                  <header>
                    <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                      {config.site?.name}
                    </h3>
                    {config.site?.role && (
                      <p className="mt-1 text-indigo-600 font-semibold">{config.site.role}</p>
                    )}
                    {config.site?.location && (
                      <p className="mt-2 flex items-center gap-1 text-sm font-medium text-gray-500">
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {config.site.location}
                      </p>
                    )}
                  </header>

                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg font-medium">
                    {config.about?.bio}
                  </p>

                  {Array.isArray(config.about?.highlights) && config.about.highlights.length > 0 && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-indigo-600 uppercase mb-4">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-accent-gradient text-white text-[10px] font-bold">{config.about.highlights.length}</span>
                        {highlightsLabel}
                      </h4>
                      <ul className="space-y-3">
                        {config.about.highlights.map((h, i) => (
                          <motion.li
                            key={h + i}
                            variants={fadeIn(0.2 + i * 0.06, 0)}
                            className="group flex gap-3 rounded-xl bg-gray-50/80 hover:bg-gray-100/90 transition-colors px-4 py-3 border border-gray-100"
                          >
                            <span className="mt-1.5 text-emerald-600" aria-hidden>✔</span>
                            <span className="text-sm font-medium text-gray-700">{h}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>

            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default About;
